import React, { useState } from "react";
import _kebabCase from "lodash/kebabcase";
import _forEach from "lodash/forEach";
import styled from "styled-components";
import Button from "Common/Button";
import Select from "Common/Select";
import Dropdown from "Common/Dropdown";
import Input from "Common/Input";
import Checkbox from "Common/Checkbox";
import Radio from "Common/Radio";
import Icon from "Common/Icon";
import TextArea from "Common/TextArea";
import { COLORS } from "~/styles/Consts";
import { styleConstructor } from "~/styles/Utils";
import {
  Form as FinalForm,
  Field as FinalFormField,
  FormSpy as FinalFormSpy,
} from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

const ButtonWrapper = styled.div`
  margin-top: 1rem;
`;

const ButtonStyled = styled(Button)`
  margin-right: 4px;
  ${({ styling }) => styleConstructor(styling)}
`;

const FieldStyled = styled(FinalFormField)`
  font-family: "Roboto", sans-serif;
  margin-bottom: 0.5rem;
  width: 100%;
  ${({ styling }) => styleConstructor(styling)}
`;

const CustomFieldArray = styled.div`
  border: 2px solid #389084;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 5px;
  position: relative;
`;

const Label = styled.label`
  font-weight: 900;
  display: block;
  margin-bottom: 2px;
  ${({ styling }) => styleConstructor(styling)}
`;

const Delete = styled.span`
  position: absolute;
  right: 1rem;
  top: 8px;
`;

const OptionMain = styled.div`
  margin-top: 5px;
`;

const Error = styled.p`
  color: ${COLORS.error};
  ${({ styling }) => styleConstructor(styling)}
`;

const Field = (params) => <FieldStyled {...params} />;

const Form = (props) => {
  const { validationSchema, onSubmit, mutators, fields, buttons, validate } =
    props;

  const [errState, setErrState] = useState({});

  const initialValues = {};
  fields.forEach(({ id, initialValue }) => {
    if (initialValue) initialValues[id] = initialValue;
  });

  const handleFormSubmit = async (vals) => {
    const isValid = await validateForm(vals);
    if (!isValid) return;
    if (onSubmit) onSubmit(vals);
  };

  const validateForm = async (vals) => {
    let isValid = true;
    if (validationSchema) {
      isValid = await validationSchema.isValid(vals);
      try {
        await validationSchema.validate(vals, { abortEarly: false });
      } catch (err) {
        const errors = err.inner.reduce(
          (formError, innerError) => ({
            ...formError,
            [innerError.path]: innerError.message,
          }),
          {}
        );
        setErrState(errors);
        return errors;
      }
    }
    return isValid;
  };

  const getComponent = (params) => {
    const { type, component, controlProps } = params;
    let c = null;
    switch (true) {
      case !!component: {
        c = component;
        break;
      }
      case type === "select": {
        c = controlProps
          ? (p) => (
              <Select
                name={p.input.name}
                value={p.input.value}
                onChange={(e, { value }) => p.input.onChange(value)}
                {...controlProps}
              />
            )
          : Select;
        break;
      }
      case type === "dropdown": {
        c = (p) => (
          <Dropdown
            name={p.input.name}
            value={p.input.value}
            onChange={(e, { value }) => p.input.onChange(value)}
            {...controlProps}
          />
        );
        break;
      }
      case type === "checkbox": {
        c = (p) => (
          <Checkbox
            name={p.input.name}
            checked={!!p.input.checked}
            onChange={(e, { checked }) => p.input.onChange(checked)}
            {...controlProps}
          />
        );
        break;
      }
      case type === "radio": {
        c = Radio;
        break;
      }
      case type === "textarea": {
        c = (p) => (
          <TextArea
            onChange={(e) => {
              const val = e.target.value || "";
              p.input.onChange(val);
            }}
            defaultValue={p.input.value}
            {...p}
          />
        );
        break;
      }
      case type === "time": {
        c = (p) => (
          <Input
            type={type}
            onChange={({ value }) => p.input.onChange(value)}
            value={p.input.value}
            {...p}
          />
        );
        break;
      }
      default: {
        c = Input;
        break;
      }
    }
    return c;
  };
  const renderError = (errorText, errorStyling) => {
    if (!errorText) return null;
    return <Error styling={errorStyling}>{errorText}</Error>;
  };
  const renderFieldArray = (params, mutators) => {
    const { push } = mutators;
    const {
      id,
      label,
      arrayName,
      autoSave,
      labelStyling,
      errorStyling,
      isArrayField,
      arrayFields,
      ...rest
    } = params;

    return (
      <>
        <FieldArray name={arrayName}>
          {(first) =>
            first &&
            first.fields &&
            first.fields.length > 0 &&
            first.fields.map((name, index) => {
              return (
                <CustomFieldArray key={name}>
                  {arrayFields &&
                    arrayFields.length > 0 &&
                    arrayFields.map((item, i) => {
                      if (item.type === "button") {
                        item.params.onClick = () =>
                          push(`${name}.${item.arrayName}`);
                      }
                      if (!item.isArrayField) {
                        if (!item.newId) {
                          item.newId = item.id;
                        }
                        item.id = `${name}.${item.newId}`;
                      }

                      return (
                        <>
                          {i === 0 && (
                            <Delete
                              onClick={() => first.fields.remove(index)}
                              style={{ cursor: "pointer" }}
                            >
                              <Icon link name="remove"></Icon>
                            </Delete>
                          )}

                          {item.type !== "button" &&
                            renderField(item, mutators)}
                          {item.arrayName && item.isArrayField && (
                            <FieldArray name={`${name}.${item.arrayName}`}>
                              {(second) =>
                                second &&
                                second.fields &&
                                second.fields.length > 0 &&
                                second.fields.map((seconditem, index2) => (
                                  <CustomFieldArray key={seconditem}>
                                    {item.arrayFields &&
                                      item.arrayFields.length > 0 &&
                                      item.arrayFields.map((item2, j) => {
                                        if (!item2.isArrayField) {
                                          if (!item2.newId) {
                                            item2.newId = item2.id;
                                          }
                                          item2.id = `${seconditem}.${item2.newId}`;
                                        }
                                        return (
                                          <>
                                            {j === 0 && (
                                              <Delete
                                                onClick={() =>
                                                  second.fields.remove(index2)
                                                }
                                                style={{ cursor: "pointer" }}
                                              >
                                                <Icon link name="remove"></Icon>
                                              </Delete>
                                            )}
                                            {renderField(item2, mutators)}
                                          </>
                                        );
                                      })}
                                  </CustomFieldArray>
                                ))
                              }
                            </FieldArray>
                          )}
                          {first?.fields?.value[index]?.questionType !==
                            "input" && (
                            <OptionMain>
                              {" "}
                              {item.type === "button" &&
                                renderButton(item, mutators)}
                            </OptionMain>
                          )}
                        </>
                      );
                    })}
                </CustomFieldArray>
              );
            })
          }
        </FieldArray>
      </>
    );
  };
  const renderField = (params, mutators) => {
    const {
      id,
      label,
      arrayName,
      autoSave,
      labelStyling,
      errorStyling,
      isArrayField,
      arrayFields,
      ...rest
    } = params;

    return isArrayField ? (
      renderFieldArray(params, mutators)
    ) : (
      <React.Fragment key={id}>
        <Label htmlFor={id} styling={labelStyling}>
          {label}
        </Label>
        <Field id={id} name={id} component={getComponent(rest)} {...rest} />
        {renderError(errState[id], errorStyling)}
      </React.Fragment>
    );
  };

  const renderButton = ({ id, text, styling, params, component }, mutators) => {
    let { push } = mutators;
    if (component) return component;
    const key = id || `${_kebabCase(text)}-btn`;

    if (params && !params.onClick && params.unique === "add") {
      params.onClick = () => push(params.push, undefined);
    }
    return (
      <ButtonStyled
        key={key}
        id={key}
        content={text}
        styling={styling}
        {...params}
        disabled={params && params.loading}
      />
    );
  };

  return (
    <FinalForm
      initialValues={initialValues}
      mutators={mutators}
      onSubmit={handleFormSubmit}
      validate={validate}
      subscription={{}}
      render={({ handleSubmit, form, submitting, pristine, values }) => {
        return (
          <form onSubmit={handleSubmit}>
            {fields.map((item) => renderField(item, form.mutators))}
            {!!buttons.length && (
              <ButtonWrapper>
                {buttons.map((item) => renderButton(item, form.mutators))}
              </ButtonWrapper>
            )}
          </form>
        );
      }}
    />
  );
};

Form.Field = Field;
Form.FieldSpy = FinalFormSpy;
Form.Label = Label;
Form.Error = Error;

Form.defaultProps = {
  fields: [],
  buttons: [],
  isArrayField: false,
  mutators: {
    setValue: ([fieldName, value], state, { changeValue }) => {
      changeValue(state, fieldName, () => value);
    },
  },
};

export default Form;
